import React from 'react';
import axios from "axios";
import { BASELINE } from '../util';

// Default table data
const macronutrient_table_data = {
    topic: "Macronutrient Overview",
    headers: ["Macronutrient", "Food Calories", "Kilojoules"], // Default headers
    rows: [
        {
            macronutrient: "Protein",
            calories: 4.0,
            kilojoules: 16.7,
        },
        {
            macronutrient: "Fat",
            calories: 9.0,
            kilojoules: 37.7,
        },
        {
            macronutrient: "Carbohydrates",
            calories: 4.0,
            kilojoules: 16.7,
        },
    ],
    options: {
        tableWidth: 400,
        cellPadding: 10,
        fontSize: 18,
        borderColor: "#000",
        borderWidth: 1,
        showAltText: false,
    },
};

// Mapping of common header names to the data property names
const DEFAULT_HEADER_MAP = {
    "Macronutrient": "macronutrient",
    "Food Calories": "calories",
    "Kilojoules": "kilojoules",
    "Element Type": "macronutrient",
    "Energy Value": "calories",
    "Power Units": "kilojoules",
    // You can add more mappings as needed
};

const Set_macronutrient_table = ({ graphData }) => {

    // Handle different data structures that might be passed
    let tableData;
    
    // Check if data is in graphData.data (from MacronutrientTable_prompt)
    if (graphData?.data?.topic) {
        tableData = graphData.data;
    } 
    // Check if data is directly in graphData (from MathGraphGeneratorModal)
    else if (graphData?.topic) {
        tableData = graphData;
    }
    // Fall back to default
    else {
        tableData = macronutrient_table_data;
    }
    
    const {
        topic,
        headers = ["Macronutrient", "Food Calories", "Kilojoules"],
        rows = [],
        options: dataOptions = {}
    } = tableData;
    
    // Merge options from the component call with options from the data
    const options = {
        ...dataOptions,
        ...(graphData?.options || {})
    };

    const {
        tableWidth = 400,
        cellPadding = 10,
        fontSize = 18,
        borderColor = "#000",
        borderWidth = 1,
        showAltText = false,
    } = options;

    // Column calculations
    const colCount = headers.length;
    const colWidth = tableWidth / colCount;

    // Create a mapping between header positions and row data properties
    // This assumes the data in rows always follows the pattern: macronutrient, calories, kilojoules
    // regardless of what headers are displayed
    const columnKeys = ["macronutrient", "calories", "kilojoules"];

    // Generate alt text
    const generateAltText = () => {
        const rowSummary = rows
            .map((row) => {
                const parts = headers.map((header, index) => {
                    const key = columnKeys[index] || "unknown";
                    return `${header}: ${row[key]}`;
                });
                return parts.join(", ");
            })
            .join("; ");

        return `Topic: ${topic}. This table shows: ${headers.join(", ")}. Data: ${rowSummary}.`;
    };

    const altText = generateAltText();
    const tableHeight = (rows.length + 1) * (fontSize * 2);

    return (
        <div>
            <svg
                width={tableWidth}
                height={tableHeight}
                xmlns="http://www.w3.org/2000/svg"
                aria-labelledby="tableDescription"
                role="img"
            >
                <title id="tableDescription">{topic}</title>
                <desc>{altText}</desc>

                {/* Header */}
                {headers.map((header, index) => (
                    <g key={`header-${index}`}>
                        <rect
                            x={index * colWidth}
                            y={0}
                            width={colWidth}
                            height={fontSize * 2}
                            fill="none"
                            stroke={borderColor}
                            strokeWidth={borderWidth}
                        />
                        <text
                            x={index * colWidth + colWidth / 2}
                            y={fontSize + cellPadding}
                            textAnchor="middle"
                            fontSize={fontSize}
                            fontFamily="serif"
                            fontStyle="italic"
                        >
                            {header}
                        </text>
                    </g>
                ))}

                {/* Rows */}
                {rows.map((row, rowIndex) => {
                    const yPos = (rowIndex + 1) * (fontSize * 2);
                    return (
                        <g key={`row-${rowIndex}`}>
                            {/* Cell borders for each column */}
                            {headers.map((_, colIndex) => (
                                <rect
                                    key={`row-rect-${rowIndex}-${colIndex}`}
                                    x={colIndex * colWidth}
                                    y={yPos}
                                    width={colWidth}
                                    height={fontSize * 2}
                                    fill="none"
                                    stroke={borderColor}
                                    strokeWidth={borderWidth}
                                />
                            ))}

                            {/* Cell contents - dynamically based on column position */}
                            {headers.map((_, colIndex) => {
                                const dataKey = columnKeys[colIndex];
                                return (
                                    <text
                                        key={`row-text-${rowIndex}-${colIndex}`}
                                        x={colIndex * colWidth + colWidth / 2}
                                        y={yPos + fontSize + cellPadding}
                                        textAnchor="middle"
                                        fontSize={fontSize}
                                    >
                                        {dataKey && row[dataKey]}
                                    </text>
                                );
                            })}
                        </g>
                    );
                })}
            </svg>

            {/* Alt text display (if enabled) */}
            {showAltText && (
                <div
                    style={{
                        marginTop: "10px",
                        fontSize: "14px",
                        color: "#666",
                        fontStyle: "italic",
                    }}
                >
                    {altText}
                </div>
            )}
        </div>
    );
};

const MacronutrientTable_prompt = async (count = 1, difficulty = 'M') => {
    // Helper function to get valid JSON from AI
    const getPerfectJSON = async (data, maxAttempts = 5) => {
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          const res = await axios.post(`${BASELINE}askai3`, data);
          if (res.data && res.data.message) {
            try {
              const ai_response = JSON.parse(res.data.message);
              return ai_response;
            } catch (error) {
              console.warn(`Attempt ${attempt}: JSON parsing error, retrying...`);
            }
          } else {
            console.warn(`Attempt ${attempt}: Unexpected response structure, retrying...`);
          }
        } catch (error) {
          console.warn(`Attempt ${attempt}: axios request error (${error.message}), retrying...`);
        }
      }
      return null;
    };
  
    // Function to generate SVG code
    const generateSVGCode = (data) => {
      if (!data) return { svgCode: "", altText: "" };
      
      // Handle different data structures
      let tableData;
      if (data.data?.topic) {
        tableData = data.data;
      } else if (data.topic) {
        tableData = data;
      } else {
        return { svgCode: "", altText: "Invalid data structure" };
      }
      
      const { topic, headers, rows, options: dataOptions = {} } = tableData;
      
      // Merge options
      const options = {
        ...dataOptions,
        ...(data.options || {})
      };
      
      const {
        tableWidth = 400,
        cellPadding = 10,
        fontSize = 18,
        borderColor = "#000",
        borderWidth = 1,
      } = options;
  
      const colCount = headers.length;
      const colWidth = tableWidth / colCount;
      const tableHeight = (rows.length + 1) * (fontSize * 2);
  
      // Define column keys for data access
      const columnKeys = ["macronutrient", "calories", "kilojoules"];
  
      // Generate alt text
      const rowSummary = rows
        .map((row) => {
          const parts = headers.map((header, index) => {
            const key = columnKeys[index] || "unknown";
            return `${header}: ${row[key]}`;
          });
          return parts.join(", ");
        })
        .join("; ");
  
      const altText = `Topic: ${topic}. This table shows: ${headers.join(", ")}. Data: ${rowSummary}.`;
  
      // SVG header section
      const headersSvg = headers
        .map((header, idx) => {
          return `
            <rect 
              x="${idx * colWidth}" 
              y="0" 
              width="${colWidth}" 
              height="${fontSize * 2}" 
              fill="none" 
              stroke="${borderColor}" 
              stroke-width="${borderWidth}"
            />
            <text
              x="${idx * colWidth + colWidth / 2}"
              y="${fontSize + cellPadding}"
              text-anchor="middle"
              font-size="${fontSize}"
              font-family="serif"
              font-style="italic"
            >
              ${header}
            </text>
          `;
        })
        .join("");
  
      // SVG rows section
      const rowsSvg = rows
        .map((row, rowIndex) => {
          const yPos = (rowIndex + 1) * (fontSize * 2);
          
          // Cell borders
          const cellBorders = headers
            .map((_, colIndex) => `
              <rect 
                x="${colIndex * colWidth}" 
                y="${yPos}" 
                width="${colWidth}" 
                height="${fontSize * 2}" 
                fill="none" 
                stroke="${borderColor}" 
                stroke-width="${borderWidth}"
              />
            `)
            .join("");
          
          // Cell contents
          const cellContents = headers
            .map((_, colIndex) => {
              const dataKey = columnKeys[colIndex];
              return `
                <text 
                  x="${colIndex * colWidth + colWidth / 2}" 
                  y="${yPos + fontSize + cellPadding}" 
                  text-anchor="middle" 
                  font-size="${fontSize}"
                >
                  ${dataKey && row[dataKey]}
                </text>
              `;
            })
            .join("");
          
          return `
            <g>
              ${cellBorders}
              ${cellContents}
            </g>
          `;
        })
        .join("");
  
      const svgCode = `
        <svg
          width="${tableWidth}"
          height="${tableHeight}"
          xmlns="http://www.w3.org/2000/svg"
          aria-labelledby="tableDescription"
          role="img"
        >
          <title id="tableDescription">${topic}</title>
          <desc>${altText}</desc>
          <!-- Table Headers -->
          ${headersSvg}
          <!-- Table Rows -->
          ${rowsSvg}
        </svg>
      `;
      return { svgCode, altText };
    };
  
    // Generate tables based on count parameter
    const tablePromises = [];
    for (let i = 0; i < count; i++) {
      // Adjust prompt based on difficulty
      let promptDifficulty = "";
      if (difficulty === 'E') {
        promptDifficulty = "Use simple, common nutrition terms and whole numbers.";
      } else if (difficulty === 'H') {
        promptDifficulty = "Use complex, specialized nutrition terminology and decimal values with multiple decimal places.";
      }
      
      const dataForAI = {
        prompt: `
  Please generate a random JSON object for a nutrition table with SHORT text elements:
  - The table should have 3 columns with SHORT, DISTINCT headers (max 10 characters each).
  - Include at least 3 rows, each containing:
    {
      "macronutrient": "short word (max 8 chars)",
      "calories": number,
      "kilojoules": number
    }
  - Use a short "topic" (max 15 characters).
  - Include "headers": ["string", "string", "string"] with 3 SHORT headers (max 10 chars each).
  - Provide standard "options" object:
    {
      "tableWidth": 400,
      "cellPadding": 10,
      "fontSize": 18,
      "borderColor": "#000",
      "borderWidth": 1,
      "showAltText": false
    }
  
  ${promptDifficulty}
  
  The final JSON must follow exactly this schema:
  
  {
    "topic": "string",
    "headers": ["string", "string", "string"],
    "rows": [
      {
        "macronutrient": "string",
        "calories": number,
        "kilojoules": number
      },
      ...
    ],
    "options": {
      "tableWidth": number,
      "cellPadding": number,
      "fontSize": number,
      "borderColor": "string",
      "borderWidth": number,
      "showAltText": boolean
    }
  }
  
  KEEP ALL TEXT SHORT - headers under 10 chars, topic under 15 chars, row values under 8 chars.
  Use Diverse Topics. Topics can be anything that relate to the table but text needs to be short. 
  Use short, simple terms.
  Output ONLY a clean JSON object, no extra text.
        `,
      };
  
      tablePromises.push(
        getPerfectJSON(dataForAI).then((ai_response) => {
          if (ai_response) {
            
            // Create a consistent data structure that works with both component patterns
            const responseData = {
              data: ai_response,
            };
            
            const { svgCode, altText } = generateSVGCode(responseData);
            
            // Return data and component reference
            return {
              data: ai_response,
              component: null, // Component is created when actually rendered
              svgCode,
              altText,
            };
          }
          return null;
        })
      );
    }
  
    const results = await Promise.all(tablePromises);
    return results.filter(Boolean); // Filter out any null results
  };

export {
    macronutrient_table_data,
    Set_macronutrient_table,
    MacronutrientTable_prompt
};